<template>
	<div class="guide-page">
		<!-- 页头 -->
		<div class="guide-hero">
			<div class="guide-hero__icon"><i class="el-icon-notebook-2"></i></div>
			<div class="guide-hero__text">
				<div class="guide-hero__title">系统使用说明</div>
				<div class="guide-hero__sub">按日常操作整理主题与锁屏、查询与权限、门店和加工订单、库存出入库、会员销售、充值退卡、提成、申购调拨及考勤薪资规则。先从左侧目录找到对应模块，遇到提示可查看末尾速查表。</div>
			</div>
		</div>

		<div class="guide-body">
			<!-- 左侧目录 -->
			<div ref="tocBox" class="guide-toc">
				<div class="guide-toc__title">目录</div>
				<div
					v-for="(sec, i) in sections"
					:key="sec.id"
					class="guide-toc__item"
					:class="{ 'guide-toc__item--active': activeSection === sec.id }"
					@click="scrollTo(sec.id)"
				>
					<span class="guide-toc__num">{{ i + 1 }}</span>
					<span>{{ sec.title }}</span>
				</div>
				<div class="guide-toc__backtop" @click="backToTop">
					<i class="el-icon-top"></i>
					<span>回到顶部</span>
				</div>
			</div>

			<!-- 正文：高度随窗口自适应，内容超出时本区域自己滚动 -->
			<div ref="contentBox" class="guide-content">
				<div v-for="sec in sections" :id="sec.id" :key="sec.id" class="guide-section">
					<div class="guide-section__head">
						<span class="guide-section__no">{{ sec.no }}</span>
						<span class="guide-section__title">{{ sec.title }}</span>
					</div>

					<div v-for="(item, j) in sec.items" :key="j" class="guide-clause">
						<div class="guide-clause__rule">
							<i class="el-icon-circle-check guide-clause__icon"></i>
							<span>{{ item.rule }}</span>
						</div>
						<div v-for="(ex, k) in item.examples" :key="k" class="guide-clause__example">
							<span class="guide-clause__example-tag">举个例子</span>
							<span>{{ ex }}</span>
						</div>
					</div>

					<!-- 常见提示语速查表 -->
					<div v-if="sec.table" class="guide-table">
						<div class="guide-table__row guide-table__row--head">
							<div class="guide-table__cell guide-table__cell--no">序号</div>
							<div class="guide-table__cell guide-table__cell--msg">系统提示</div>
							<div class="guide-table__cell">是什么意思 / 怎么处理</div>
						</div>
						<div v-for="(row, r) in sec.table" :key="r" class="guide-table__row">
							<div class="guide-table__cell guide-table__cell--no">{{ r + 1 }}</div>
							<div class="guide-table__cell guide-table__cell--msg">{{ row.msg }}</div>
							<div class="guide-table__cell">{{ row.how }}</div>
						</div>
					</div>

					<div v-if="sec.tip" class="guide-section__tip">
						<i class="el-icon-info"></i>
						<span>{{ sec.tip }}</span>
					</div>
				</div>

				<div class="guide-footer">
					本说明于 2026 年 10 月 16 日更新，按当前系统实现整理。可用菜单与操作以账号权限、单据状态和部署版本为准；遇到差异请记录单号与提示并联系管理员。
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import systemRules from "./systemRules";
export default {
	name: "UsageGuide",
	data() {
		return {
			activeSection: "sec-theme",
			// 点击目录触发的平滑滚动期间，暂停「由滚动位置反推高亮」，避免高亮乱跳
			isManualScroll: false,
			// scroll 事件节流开关（一帧最多算一次）
			scrollTicking: false,
			// 手动跳转的延时器句柄
			manualTimer: null,
			sections: [
				...systemRules,
				{
					id: "sec-order",
					no: "01",
					title: "销售订单的基本规则",
					items: [
						{
							rule: "销售订单保存后为待确认；确认时才扣减会员卡余额及商品库存。确认后仍有欠款的进入欠款状态，款项收齐才是已完成。销售提成需在订单完成后单独点击「创建提成」，不会随确认自动生成。",
							examples: ["订单收费 300 元、已收 100 元：确认后仍欠 200 元；补齐后才变为已完成。"]
						},
						{
							rule: "同一种支付方式，一笔订单只能用一次。想组合付款没问题，但每种方式只能出现一笔。",
							examples: [
								"一笔 300 元的订单，不能用会员卡刷两次；但可以「现金 100 元 + 会员卡 200 元」这样组合支付。"
							]
						},
						{
							rule: "会员卡销，就是直接用会员卡里的余额支付货款。一笔订单最多用一次卡销。",
							examples: [
								"顾客卡里有 500 元，买 300 元的商品时选择卡销，确认后卡里剩 200 元。"
							]
						},
						{
							rule: "同一顾客仍有待确认的销售订单时，不能再新建卡销订单；同一会员卡也不能同时存在多笔未确认卡销单。先核对原单，真实成交的按流程确认，误开的未确认单可删除。",
							examples: []
						},
						{
							rule: "同一个会员，当天已经开过一笔金额相同、并且还没确认的订单时，新订单开不出来。这是为了防止重复开单。",
							examples: [
								"上午开了一笔 500 元的订单没确认，下午又给同一位顾客开一笔 500 元 → 系统会提示「会员存在相同金额的未完成订单」；先核对上午那笔是否重复，再按真实业务确认或处理原单，不要为通过校验而修改金额。"
							]
						},
						{
							rule: "未确认的销售订单可以修改、删除；修改时重新校验支付明细、卡余额及重复订单。已确认订单不能直接修改、删除。销售欠款可分次补收，每次金额须大于 0 且不超过剩余欠款；支持微信、支付宝、现金、刷卡、抖音核销，不支持卡销补款。",
							examples: []
						}
					],
					tip: "销售订单依次核对：保存 → 确认扣卡及扣库存 → 有欠款则补收 → 完成后创建提成。报表日期口径见统计章节。"
				},
				{
					id: "sec-twostep",
					no: "02",
					title: "会员卡销的「两步走」",
					items: [
						{
							rule: "下单时校验会员卡可用余额，但不实际扣卡、不扣库存；删除未确认订单会删除关联明细和收款记录，不产生会员卡退款。",
							examples: []
						},
						{
							rule: "未确认卡销单不会冻结或预扣余额，但会阻止同一张卡再次开未确认卡销单。确认时仍按当时的可用余额重新检查。",
							examples: []
						},
						{
							rule: "未确认卡销单可以确认、删除，或修改为其他支付方式。修改支付方式只解除卡销占位；如果该顾客仍有待确认销售单，新开卡销单仍会被拦截。",
							examples: ["改成现金后，仍需按真实业务处理原单；不能仅靠改支付方式绕过同一顾客待确认订单的限制。"]
						},
						{
							rule: "确认时同时核对可用余额、会员卡信息和商品库存，校验通过后扣卡并生成消费记录。若余额或库存不足，按提示核对真实收款、充值欠款和库存后再确认，不应虚改订单金额。",
							examples: []
						},
						{
							rule: "确认操作中的扣卡与库存变动在同一事务内处理，失败时不会保留半笔扣款。刷新核对订单状态和消费记录，排除具体失败原因后再操作。",
							examples: []
						}
					],
					tip: "保存不扣卡、不冻结金额；确认才扣卡，且同时检查库存。"
				},
				{
					id: "sec-arrears",
					no: "03",
					title: "有欠款时，卡里「能花的钱」会变少",
					items: [
						{
							rule: "什么是欠款：充值时钱没收齐，先给会员把充值记到卡上，就产生一笔「欠款的充值订单」。",
							examples: []
						},
						{
							rule: "卡销可用余额＝卡余额－最近一笔已确认且仍欠款的充值单欠款金额－该笔赠送金额，按两位小数计算，最低为 0；无这类欠款充值单时取卡余额。",
							examples: ["余额 2000 元、充值欠款 800 元、该笔赠送 200 元，可用余额为 1000 元。"]
						},
						{
							rule: "为什么要这样算：欠款的充值是「先记账、后收钱」，卡里的余额有一部分其实来自还没收到的钱，这部分（连同赠送）不能先花掉。",
							examples: []
						},
						{
							rule: "新充值会拦截已有的已确认欠款充值单。可用余额计算取符合条件的充值单中创建时间最近的一笔，并不是累加历史全部欠款；遇到历史数据存在多笔欠款时，应先请负责人核对。",
							examples: []
						},
						{
							rule: "保存、修改和确认卡销订单都会校验可用余额。部分补缴充值欠款会减少不可用金额；赠送金额在该笔充值欠款全部补清后才解除这项限制。",
							examples: []
						}
					],
					tip: "卡余额 ≠ 能花的钱。有欠款时，能花的钱会少一截。"
				},
				{
					id: "sec-recharge",
					no: "04",
					title: "会员充值与欠款规则",
					items: [
						{
							rule: "充值要确认后才到账，赠送的金额也是确认后才一起进卡。",
							examples: []
						},
						{
							rule: "充值确认时，充值金额与赠送金额全部记入卡余额；未收齐的部分保留为充值欠款，可用余额仍受欠款及赠送金额限制。补款只减少欠款，不会再次增加卡余额。",
							examples: []
						},
						{
							rule: "一张会员卡，同一时间只能有一笔欠款。欠款没补清之前，这张卡不能再充值。",
							examples: [
								"9 月 1 日会员充 5000 元只付了 3000 元（欠 2000 元）；9 月 3 日想再充 1000 元，系统会提示「该会员卡存在欠款的充值订单【单号】，请先补缴欠款后再充值！」；把 2000 元补清之后才能继续充值。"
							]
						},
						{
							rule: "除了欠款，只要这张卡上还有没确认的充值单，同样不能再充值。先把上一笔确认掉。",
							examples: [
								"上午刚给会员开了一笔充 1000 元的单子还没确认，下午又想再充一笔 → 系统会提示「存在未完成的会员充值记录」，先去把上午那笔确认掉。"
							]
						},
						{
							rule: "充值不能用「会员卡销」来付。充值是往卡里加钱，卡销是从卡里扣钱，这两件事不能混在一起，所以充值时支付方式里本来就选不到卡销。",
							examples: [
								"给会员充值 2000 元，支付方式只能选现金、微信、支付宝、银行卡这些，没有「会员卡销」这一项；只有买东西开销售订单时才能用卡销。"
							]
						},
						{
							rule: "已确认充值单不能修改、删除。欠款可以分次补缴，每次大于 0，累计付款不得超过充值金额，且不允许卡销补款。已完成充值单有单独的「退卡」流程，要求卡余额足以扣回该笔充值金额与赠送金额，不能把它与整张卡退卡混为一谈。",
							examples: ["充值 1000 元赠 200 元，确认后余额增加 1200 元；后续补齐欠款不会再加 1000 元。该笔充值退回时需扣除 1200 元卡余额，退款记录本金为 1000 元，赠送 200 元另记。"]
						}
					],
					tip: "先核对充值是否确认、是否欠款，再选择修改、补款或充值记录退回；整卡退卡的清零规则见下一节。"
				},
				{
					id: "sec-return",
					no: "05",
					title: "会员退卡、解绑与换卡",
					items: [
						{
							rule: "退卡前，系统会自动检查这张卡：还有没确认完的充值单 → 不能退；还有欠款充值订单 → 不能退，要先补缴。",
							examples: [
								"会员上午刚充了值还没确认，就来退卡 → 系统拒绝，先把那笔充值单确认或处理完。",
								"会员卡上挂着 2000 元充值欠款 → 系统拒绝退卡，补缴清欠款后才能退。"
							]
						},
						{
							rule: "会员卡页面的整卡退卡：退款金额允许为 0 且不能超过现有余额，0 元退卡需要额外确认，但操作成功后会清零整张卡余额，即使填写的退款金额小于余额，也不会保留差额。系统记录退前余额与实际退款金额。",
							examples: ["卡余额 300 元，退款填写 200 元：退卡后余额是 0 元，不是 100 元。操作前须核对并向顾客说明结算金额。"]
						},
						{
							rule: "整卡退卡不会自动解绑或换卡。旧卡余额清零后，再按实际业务单独操作解绑或换卡；余额本来为 0 时无需退卡，但仍要通过充值记录检查。",
							examples: []
						},
						{
							rule: "换卡、解绑同样要过上面那两道检查：只要这张储值卡上还挂着未确认的充值单、或者欠款单，就不能换卡、不能解绑。",
							examples: [
								"会员卡上有一笔欠 1500 元的充值单，想直接换张新卡绕过 → 系统会拒绝，必须先补缴欠款。"
							]
						}
					],
					tip: "先处理未确认充值及欠款，再核对退款金额；整卡退卡会清零全部余额。充值记录退回只扣回对应充值及赠送，两者不同。实际资金退款须核对支付渠道。"
				},
				{
					id: "sec-commission",
					no: "06",
					title: "提成规则",
					items: [
						{
							rule: "销售订单须已确认且已完成，才能点击「创建提成」，已创建的不能重复创建。无卡销支付时按订单收费金额的 4% 计算提成池；只要订单含卡销支付，即使混合其他支付方式，也不生成销售提成。",
							examples: ["收费 2000 元，全部现金支付时提成池为 80 元；现金 1000 元＋卡销 1000 元，该单没有销售提成。"]
						},
						{
							rule: "充值提成（按充值金额分档）：不足 1000 元提 4%；1000 元（含）至不足 5000 元提 6%；5000 元（含）及以上提 8%。",
							examples: [
								"充 800 元 → 提成 800 × 4% = 32 元；充 3000 元 → 提成 3000 × 6% = 180 元；充 8000 元 → 提成 8000 × 8% = 640 元。"
							]
						},
						{
							rule: "有欠款的充值订单，提成不在「确认」时生成：确认只把参与员工记到订单上，等欠款补足（补款完成）时才生成提成，提成时间按补足欠款的时间算。无欠款、确认即完成的充值订单，提成仍在确认时生成。",
							examples: [
								"9 月 1 日会员充 5000 元只付了 3000 元（欠 2000 元）并确认 → 当天不生成提成；9 月 10 日补缴 2000 元欠款 → 这时才生成 5000 × 8% = 400 元的充值提成，提成时间记为 9 月 10 日。",
								"一笔充值确认时钱已收齐（无欠款）→ 提成在确认当天生成，和原来一样。"
							]
						},
						{
							rule: "「是否可发放」按关联业务是否完成显示，与「是否已发放」是不同概念。真正发放时会再次检查关联充值单或销售单的欠款：仍有欠款的记录跳过，弹窗逐条列明单号、员工、金额与原因，其余可发放记录继续处理。退单、退卡产生的负数冲正提成不受欠款拦截。",
							examples: [
								"筛选「是否可发放=可发放」→ 列出业务已完成的提成；点发放时若其中某条关联的充值订单仍有欠款，系统当场拦截并弹窗列明细，其余照常发放。",
								"会员把欠款补清后直接点发放 → 系统当场检测到欠款已清，正常发放成功，不必先点其他按钮。"
							]
						},
						{
							rule: "卡销订单执行「创建提成」时才检查充值提成重算：当前卡余额低于 500 元、该卡最近一笔充值与销售订单的创建日期同一天，且该充值已有提成记录，才撤回原提成并按该笔充值金额的 4% 重建。不是每次卡销扣款都会自动重算。",
							examples: ["充值单与销售单同一天创建，执行创建提成时余额 499 元，且满足其余条件：原充值提成按 4% 重建；余额恰好 500 元不触发此条件。"]
						},
						{
							rule: "有电话提成时，先从总提成池分出 20% 给电话提成员工，其余由所选参与员工均分并保留两位小数；无电话提成则直接均分。提成生成与发放是两个操作，销售提成日期按点击创建时记录，充值提成按确认或补清欠款的生成时点记录。",
							examples: []
						}
					],
						tip: "销售完成后手动创建；充值无欠款时确认生成、有欠款时补清生成。退回或重算涉及原提成时，未发放记录删除，已发放记录保留并生成负数冲正。"
				},
				{
					id: "sec-cancel",
					no: "07",
					title: "退单规则",
					items: [
						{
							rule: "只有「已完成」的订单才能退单。确认之后钱还没收齐的订单（还挂着欠款），系统不允许退。",
							examples: [
								"一笔订单确认时只收了一部分钱、剩下的挂着欠条 → 这笔单子不能退，先把钱收齐再处理。"
							]
						},
						{
							rule: "销售退单会返还该单商品库存，按原支付记录生成负数退款记录；卡销部分加回会员卡余额并调整消费记录。该单未发放提成删除，已发放提成保留并新增负数冲正。现金、微信等实际退款还需在对应渠道核对处理，系统退款记录不代表外部到账。",
							examples: []
						},
						{
							rule: "还没确认的订单不用退，直接删除就行。",
							examples: []
						}
					],
					tip: "退单只针对已完成订单；未确认订单走删除，欠款未清的订单先收齐钱。"
				},
				{
					id: "sec-subscription",
					no: "08",
					title: "门店申购单（向采购部要货）",
					items: [
						{
							rule: "申购单是门店向采购部「要货」的单子。一张单子可以要多种商品，但同一种商品只能填一行。",
							examples: [
								"一张单子里把「隐形眼镜 A」填了两行 → 系统会提示「申购明细中存在相同的商品，请检查！」。想要更多就直接把数量改大。"
							]
						},
						{
							rule: "申购商品必须已有本门店的商品库存档案，不要求当前库存大于 0；未建立档案的商品先维护门店商品，再发起申购。",
							examples: []
						},
						{
							rule: "每张申购单都要走六步：待提交 → 采购待确认 → 待审核 → 待发快递 → 快递中 → 已完成。",
							examples: [
								"①门店新建并「提交」→ ②采购部填好成本和运费后「确认」→ ③审核人「审核通过」→ ④采购部录入快递单号 → ⑤门店收到货点「接收」→ ⑥状态变「已完成」，货才真正进库存。"
							]
						},
						{
							rule: "审核不通过不会被作废：状态会变回「审核不通过」，采购部改完后重新确认，就又回到「待审核」可以再送审。",
							examples: [
								"审核人觉得成本填高了，填了原因点不通过 → 采购部改成本后重新确认 → 单据回到「待审核」，再送一次审核即可。"
							]
						},
						{
							rule: "门店修改商品、数量仅限「待提交」；删除仅限「待提交」「采购待确认」。审核不通过只允许采购侧修改成本、运费等采购信息后重新确认，不会回到门店可修改商品数量的状态。不要把审核退回当作修改数量的通道。",
							examples: []
						},
						{
							rule: "接收仅限「快递中」。逐行填写实际数量，必须大于等于 0 且不超过申购数量，有效接收明细还需快递信息；库存按实际接收量增加。接收成功后整单转为已完成，不会为少到的数量保留待接收状态。",
							examples: ["申购 10 个、实际收 8 个：库存加 8，整单完成；另 2 个不能在同一已完成单上再次接收，先核对后续供货安排。"]
						},
						{
							rule: "财务付款在「审核通过」之后随时都能做（待发快递、快递中、已完成都行），付款不会改变单据状态，只是打上付款时间标记；付过一次就不能再付。",
							examples: [
								"货还没到就要先给供应商打款 → 单据在「待发快递」状态时就可以点付款，不影响后面正常发货和接收。"
							]
						},
						{
							rule: "退货只对「已完成」的申购单开放；退货数量既不能超过当时实际收到的数量，也不能超过门店现在的库存。",
							examples: [
								"申购 10 个、实际收到 10 个、已经卖掉 3 个 → 最多只能退 7 个，系统会按这两个数里较小的那个卡住。"
							]
						},
						{
							rule: "退货之后还有一条换货链：门店退货（库存立刻减少）→ 门店填快递单号发回 → 采购部收到 → 采购部发出换货 → 门店再次「接收」，这时换回的商品才回到库存。",
							examples: [
								"退了 2 个镜片，门店库存当场就少 2 个；半个月后采购部把新镜片寄回来，门店点了「接收」，库存才加回 2 个。中间这段时间库存是偏少的，这是正常的。"
							]
						}
					],
					tip: "记两句话：货在门店点「接收」那一刻才进库存；钱在审核通过之后随时能付，付款不改状态。"
				},
				{
					id: "sec-transfer",
					no: "09",
					title: "商品调拨（门店之间调货）",
					items: [
						{
							rule: "调拨就是门店之间互相调货，建单时要选「调出门店」和「调入门店」，单号以 PDB 开头。",
							examples: []
						},
						{
							rule: "保存调拨单的那一刻，调出门店的库存就立刻减掉了；调入门店要等点了「接收」之后才增加。中间这段时间货算「在途」，两边库存里都看不到。",
							examples: [
								"A 店调 5 个镜盒给 B 店：单子一保存，A 店库存当场少 5 个；B 店要等收到货点了「接收」，库存才多 5 个。"
							]
						},
						{
							rule: "调拨也是三步走：调出门店建单（库存立刻减）→ 确认 → 调入门店接收（库存才加）。必须先确认才能接收，两步都不能重复做。",
							examples: []
						},
						{
							rule: "⚠️ 有三种商品会被「跳过」不调拨，但现在系统会明确提示，不再悄悄丢失：①调出门店没有这个商品的库存记录 ②调入门店没有这个商品的档案 ③调出门店库存不够。保存调拨单后，凡被跳过的商品，web 端都会弹出一个提示框，逐条列出商品名称、规格、申请数量和未调拨原因（含具体门店与当前库存）。",
							examples: [
								"一张单子调 3 种商品，其中 1 种调入门店从来没卖过（没有商品档案）→ 保存后弹窗提示「有 1 种商品未调拨」，并列出该商品及原因「调入门店【B 店】无商品【…】的档案」。",
								"A 店库存只有 2 个，却填了调 5 个 → 弹窗提示该商品「调出门店【A 店】商品【…】库存不足（当前库存 2，申请调拨 5）」。"
							]
						},
						{
							rule: "所以保存之后如果出现「有 N 种商品未调拨」的提示，一定要点开看清单：被跳过的商品不会进调拨单，要补齐档案或库存后重新调拨。",
							examples: [
								"填了 4 种，提示「共调拨 3 种，有 1 种未调拨」→ 点开提示框，看少的那一种是什么原因（调入门店缺档案 / 调出门店库存不足）。"
							]
						},
						{
							rule: "如果所有商品都被跳过，系统会直接提示「调拨失败，没有检测成功需要调拨的商品，请检查后重试！」，这时一张调拨单都不会生成，调出门店的库存也不会变。",
							examples: []
						},
						{
							rule: "调拨数量必须大于 0，小于等于 0 会被拒绝。",
							examples: []
						},
						{
							rule: "未确认且未接收的调拨单可以删除，删除会返还调出门店库存；已确认或已接收不能删除。需要反向调拨时，应先完成原单实际交接和接收，确保反向调出方已有对应库存，再按实际退回数量建单。",
							examples: []
						},
						{
							rule: "接收时如果调入门店缺这个商品的档案，接收会失败并提示「调入门店没有【…】信息」，需要先把商品加到调入门店再接收。",
							examples: []
						}
					],
					tip: "调拨的风险点是「部分商品被跳过」：数量对不上不会整体报错，只会少调几种。好在保存后 web 端会弹窗逐条列出被跳过的商品及原因，务必点开核对，确认无误再关掉。"
				},
				{
					id: "sec-statistic",
					no: "10",
					title: "统计口径小贴士",
					items: [
						{
							rule: "门店日统计的销售额按销售单创建日期归属，实收与补款按收款记录创建日期分列统计，卡销单独列示。该报表的销售额查询并不以已确认为统一前提，因此不能把所有统计都理解成「确认后才入账」。",
							examples: []
						},
						{
							rule: "会员卡实际扣款和消费记录在销售确认时生成，但订单收款记录可以在开单时已建立；跨日确认时，收款统计日期与实际扣卡日期可能不同。补款按补款记录时间归属，退单按退款记录时间记录。对账应同时核对订单、收款、会员卡流水和所选报表口径。",
							examples: []
						},
					{
						rule: "「订单统计」里的「出入库统计」页签：按【上线商品类型(镜片类型)】×【年度】统计 1~12 月入库数量与出库数量，按实际入库、出库日期分别归属月份，全部门店汇总；表格每行一个上线商品类型，每月下分「入/出」两子列，末尾为全年合计，并带合计行。切换年份即按该年实际出入库重算。",
						examples: []
					}
				]
				},
				{
					id: "sec-faq",
					no: "11",
					title: "常见提示速查表",
					items: [],
					table: [
						{"msg":"请输入 3 位或 6 位十六进制色值，例如 #2E5AA8","how":"检查主题色输入，只使用 3 位或 6 位十六进制颜色，或直接点预设色／取色器。"},
						{"msg":"两次输入的锁屏密码不一致","how":"重新输入相同的本次密码；设置完成后才会真正锁屏。"},
						{"msg":"密码错误，请等待 30 秒后重试","how":"连续输错 5 次后进入等待期，倒计时结束再输入本次锁屏密码；忘记密码可返回登录。"},
						{"msg":"订单状态已变化，请刷新后重新操作！","how":"先刷新并核对当前状态、出库记录及其他人员处理情况，不要直接新建重复单据。"},
						{"msg":"无回退出库权限，请联系管理员授权！","how":"回退出库需要单独权限，请由管理员核对角色与按钮授权。"},
						{"msg":"该出库单已退回，不重复返还库存。","how":"原单已回退成功，库存不会再次增加。若仍有差异，请核对库存变动记录和原出库单。"},
						{"msg":"商品库存记录不存在或不唯一，请先核对库存！","how":"请管理员核对商品及库存档案，修正数据后再进行业务操作，不要重复创建出库。"},
						{"hidden":true,"msg":"加工明细必须为两条：右眼、左眼各一片镜片！","how":"核对独立加工订单明细，必须一左一右，商品、数量和轴位均应有效。"},
						{"hidden":true,"msg":"该订单存在未入库的采购单，请先处理采购单！","how":"先查对应采购单并完成实际采购／入库处理，再根据订单状态执行下一步。"},
						{"msg":"打卡过于频繁，请 N 分钟后再试！","how":"按提示等待，管理员可配置打卡间隔；不要连续重复点击。"},
						{
							msg: "储值卡可用余额【X元】不足，请选择其他支付方式！",
							how: "这张卡现在能花的钱不够。如果有欠款，可用余额会比卡余额少。办法：让会员先补缴欠款、改用其他支付方式，或在同一订单中使用不同支付方式组合付款；每种方式仍只能出现一行。"
						},
						{
							msg: "该会员卡存在未确认的卡销订单【单号】，请先确认或取消该订单后再下单！",
							how: "这张卡上有一笔用卡销开的单子还没确认，占着位置。去销售订单列表把那笔单确认掉、删掉，或者把卡销改成别的支付方式。"
						},
						{
							msg: "会员存在未完成订单【单号】，请先完成该订单！",
							how: "这个会员名下还有没确认的订单（不一定是用会员卡付的）。先把那笔确认掉，才能开新的卡销单。"
						},
						{
							msg: "会员存在相同金额的未完成订单【单号】，请先完成该订单！",
							how: "今天已经开过一笔金额一模一样、还没确认的单子。先核对原单，按真实业务确认或处理重复记录，不要为绕过检查改金额。"
						},
						{
							msg: "会员卡销仅能支付一笔！",
							how: "一笔订单里卡销只能填一行。把两行卡销合并成一行。"
						},
						{
							msg: "同一种支付方式只允许使用一次！",
							how: "现金、微信等同一种方式在一笔订单里只能出现一行。把重复的两行合并，或者改用组合支付。"
						},
						{
							msg: "实际收款金额不能超过订单金额！",
							how: "支付明细里收的钱比订单金额多了。一行一行核对支付金额。"
						},
						{
							msg: "支付金额不能为0",
							how: "至少要填一笔支付方式，而且金额要大于 0。"
						},
						{
							msg: "该会员卡存在欠款的充值订单【单号】，请先补缴欠款后再充值！",
							how: "这张卡上一笔充值还没收齐钱。先做「补款」把欠款结清，才能再充值。"
						},
						{
							msg: "存在未完成的会员充值记录，不允许再次充值！",
							how: "有一笔充值单还没确认。先去把它确认掉，再充下一笔。"
						},
						{
							msg: "充值不允许使用会员卡销支付！",
							how: "充值不能用会员卡里的钱来付。换成现金、微信、支付宝或银行卡。"
						},
						{
							msg: "该储值卡存在未确认的充值订单【单号】，不允许退卡 / 换卡 / 解绑！",
							how: "先把那笔没确认的充值单确认掉，再来做退卡、换卡或解绑。"
						},
						{
							msg: "该储值卡存在欠款的充值订单【单号】，请先补缴欠款后再退卡 / 换卡 / 解绑！",
							how: "先补缴欠款，再做退卡、换卡或解绑。"
						},
						{
							msg: "会员充值记录已经确认，不允许修改 / 删除！",
							how: "确认过的充值单不能直接修改或删除。先核对实际收款与卡余额，再联系负责人处理，不要重复充值。"
						},
						{
							msg: "退卡金额不能小于0且不能超过储值卡余额！",
							how: "核对退前余额和实际退款金额；整卡退卡会清零全部余额，退款填少不会保留差额。"
						},
						{
							msg: "储值卡余额为0，无需退卡！",
							how: "卡里已经没钱了，不需要退卡，直接解绑或换卡即可。"
						},
						{
							msg: "会员充值记录未完成，不允许退卡，可以直接删除！",
							how: "未完成不一定是未确认。未确认单可删除；已确认且欠款的单不能删除，需核对并补清欠款后再按可用流程处理。"
						},
						{
							msg: "会员余额不足，无法退卡！",
							how: "充值记录退回要求余额足以扣除该笔充值本金与赠送金额之和。核对是否已经消费，不要把本金金额当作全部扣减额。"
						},
						{
							msg: "申购明细中存在相同的商品，请检查！",
							how: "同一种商品在一张申购单里只能填一行。把重复的两行合并，直接改数量就行。"
						},
						{
							msg: "申购的商品中有不存在门店库存中的商品，请检查！",
							how: "只能申购门店库存里已经有的商品。从没卖过的要先在门店商品里加上。"
						},
						{
							msg: "申购的商品数量必须大于0，请检查！",
							how: "明细里某一行数量填了 0 或空，改成实际要货数量。"
						},
						{
							msg: "当前申购单状态不允许修改！",
							how: "商品与数量只在待提交时可修改。采购待确认时可删除后按实际业务重建；审核不通过仅开放采购信息重填，不开放门店商品数量修改。"
						},
						{
							msg: "当前申购单状态不允许删除！",
							how: "只有「待提交」和「采购待确认」能删。再往后（待审核及之后）就删不掉了。"
						},
						{
							msg: "当前申购单状态不允许接收！",
							how: "只有「快递中」能点接收。先让采购部把快递单号录进去。"
						},
						{
							msg: "存在未填写实际数量的申购单明细，不允许接收！",
							how: "每一行的「实际数量」都要填，确实没到货就填 0。"
						},
						{
							msg: "商品信息：【名称；规格】最多只允许接收【N】个！",
							how: "实际收货数量不能超过申购数量。多到的部分要另外补一张申购单。"
						},
						{
							msg: "商品信息：【名称；规格】未有快递信息，不允许接收！",
							how: "这一行还没有快递单号，让采购部先录入单号再接收。"
						},
						{
							msg: "当前申购单状态不允许财务付款！",
							how: "要等审核通过之后（待发快递 / 快递中 / 已完成）才能付款。"
						},
						{
							msg: "当前申购单已经财务付款，不允许重复操作！",
							how: "这笔已经付过款了，不用再付一次。"
						},
						{
							msg: "申购单未完成，不允许退货！",
							how: "只允许已完成申购单退货。未实际收货不要为退货而虚假接收，先核对物流和供货情况。"
						},
						{
							msg: "申购的商品【名称；规格】最多只允许退【N】个！",
							how: "退货数量不能超过当时实际收到的数量。"
						},
						{
							msg: "申购的商品【名称；规格】，库存数量只有N个，最多只允许退【N】个！",
							how: "按真实可退货物与现存库存填写数量，不得用虚假入库补足退货数量。"
						},
						{
							msg: "调拨失败，没有检测成功需要调拨的商品，请检查后重试！",
							how: "所有商品都被跳过了。常见原因：调入门店没有这些商品的档案、调出门店库存不够。检查后重试。"
						},
						{
							msg: "调拨的商品数量不能小于等于0！",
							how: "明细里某一行数量填了 0，改成实际调拨数量。"
						},
						{
							msg: "调拨记录已确认，不允许删除！ / 调拨记录已被调入门店接收，不允许删除！",
							how: "确认或接收后不能删除。核对原单实际交接，完成接收后才有调入方库存可供反向调拨。"
						},
						{
							msg: "调拨记录未确认，不允许操作接收！",
							how: "调入门店要等调出门店先点「确认」之后才能接收。"
						},
						{
							msg: "调拨记录已被调入门店接收，请勿重复接收！",
							how: "这笔已经接收过了，库存早就加上了，不要重复点。"
						},
						{
							msg: "调入门店没有【类型：…;名称：…；规格：…】信息！",
							how: "接收时调入门店缺这个商品的档案。先把商品加到调入门店，再来接收。"
						}
					],
					tip: "提示里带的【单号】就是出问题的那笔单子。复制单号去对应的列表页搜索，就能直接找到它。"
				}
			].filter(section => !section.hidden).map((section, index) => ({
				...section,
				no: String(index + 1).padStart(2, "0"),
				table: section.table ? section.table.filter(row => !row.hidden) : undefined
			}))
		};
	},
	mounted() {
		this.bindScroll();
		this.$nextTick(this.syncActiveSection);
	},

	// 页面被 keepAlive 缓存，切回标签页时重新对齐一次高亮
	activated() {
		this.$nextTick(this.syncActiveSection);
	},

	watch: {
		// 窗口很矮时目录自身会滚动，保证当前项始终可见
		activeSection() {
			this.$nextTick(this.scrollTocIntoView);
		}
	},

	beforeDestroy() {
		this.unbindScroll();
	},

	methods: {
		bindScroll() {
			const box = this.$refs.contentBox;
			if (box) {
				box.addEventListener("scroll", this.handleScroll, { passive: true });
			}
			window.addEventListener("resize", this.handleScroll);
		},

		unbindScroll() {
			const box = this.$refs.contentBox;
			if (box) {
				box.removeEventListener("scroll", this.handleScroll);
			}
			window.removeEventListener("resize", this.handleScroll);
			if (this.manualTimer) {
				clearTimeout(this.manualTimer);
				this.manualTimer = null;
			}
		},

		// 节流：一帧最多同步一次高亮
		handleScroll() {
			if (this.scrollTicking) {
				return;
			}
			this.scrollTicking = true;
			window.requestAnimationFrame(() => {
				this.scrollTicking = false;
				this.syncActiveSection();
			});
		},

		// 取章节元素：限定在当前内容区内查找，避免多标签页时 id 撞车
		getSectionEl(id) {
			const box = this.$refs.contentBox;
			return box ? box.querySelector("#" + id) : null;
		},

		// 由内容区的滚动位置，反推当前应该高亮哪一节
		syncActiveSection() {
			const box = this.$refs.contentBox;
			if (!box) {
				return;
			}
			if (this.isManualScroll) {
				return;
			}

			// 已经滚到底：直接高亮最后一节。
			// 否则最后一节可能因为高度不够，永远到不了判定线，导致高亮卡在倒数第二节。
			if (box.scrollTop + box.clientHeight >= box.scrollHeight - 4) {
				this.activeSection = this.sections[this.sections.length - 1].id;
				return;
			}

			// 判定线：容器顶部往下 24px，章节顶边越过它就认为「进入这一节」
			const line = box.getBoundingClientRect().top + 24;
			let current = this.sections[0].id;
			for (let i = 0; i < this.sections.length; i++) {
				const el = this.getSectionEl(this.sections[i].id);
				if (!el) {
					continue;
				}
				if (el.getBoundingClientRect().top <= line) {
					current = this.sections[i].id;
				} else {
					break;
				}
			}
			this.activeSection = current;
		},

		scrollTo(id) {
			const box = this.$refs.contentBox;
			const el = this.getSectionEl(id);
			if (!box || !el) {
				return;
			}

			this.activeSection = id;
			// 手动跳转期间暂停反推，等平滑滚动结束再恢复
			this.isManualScroll = true;
			if (this.manualTimer) {
				clearTimeout(this.manualTimer);
			}
			this.manualTimer = setTimeout(() => {
				this.isManualScroll = false;
				this.manualTimer = null;
				this.syncActiveSection();
			}, 600);

			const top = el.getBoundingClientRect().top - box.getBoundingClientRect().top + box.scrollTop - 12;
			box.scrollTo({ top: Math.max(top, 0), behavior: "smooth" });
		},

		scrollTocIntoView() {
			const toc = this.$refs.tocBox;
			if (!toc) {
				return;
			}
			const active = toc.querySelector(".guide-toc__item--active");
			if (!active) {
				return;
			}
			const top = active.offsetTop;
			const bottom = top + active.offsetHeight;
			if (top < toc.scrollTop) {
				toc.scrollTop = top;
			} else if (bottom > toc.scrollTop + toc.clientHeight) {
				toc.scrollTop = bottom - toc.clientHeight;
			}
		},

		// 回到顶部（滚动发生在内容区内部，不是 body）
		backToTop() {
			this.activeSection = this.sections[0].id;
			const box = this.$refs.contentBox;
			if (box) {
				box.scrollTo({ top: 0, behavior: "smooth" });
			}
		}
	}
};
</script>

<style scoped>
/* 页面不写死高度：直接贴合标签页容器（.router-tab__container，position:relative），
   用 absolute 四边定位而不是 height:100% —— 中间还有缓存层会打断百分比高度链。
   页面内部再用 flex 分配：页头固定，右侧正文吃满剩余空间并自己滚动
   （外层容器是 overflow:hidden，靠外层滚动是滚不动的） */
.guide-page {
	box-sizing: border-box;
	position: absolute;
	top: 0;
	right: 0;
	bottom: 0;
	left: 0;
	padding: 16px 20px;
	display: flex;
	flex-direction: column;
	overflow: hidden;
}

/* 页头 */
.guide-hero {
	display: flex;
	align-items: center;
	flex-shrink: 0;
	padding: 22px 26px;
	border-radius: 12px;
	background: linear-gradient(90deg, var(--app-primary-active) 0%, var(--app-primary-active) 100%);
	color: var(--app-primary-contrast);
	box-shadow: 0 6px 18px rgba(30, 64, 175, 0.22);
}

.guide-hero__icon {
	width: 52px;
	height: 52px;
	border-radius: 12px;
	background: rgba(255, 255, 255, 0.16);
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 28px;
	margin-right: 18px;
	flex-shrink: 0;
}

.guide-hero__title {
	font-size: 20px;
	font-weight: 600;
	letter-spacing: 1px;
}

.guide-hero__sub {
	margin-top: 6px;
	font-size: 13px;
	line-height: 1.7;
	color: inherit;
	opacity: 0.85;
	max-width: 900px;
}

/* 布局：目录固定宽度，剩余空间全给正文 */
.guide-body {
	display: flex;
	align-items: stretch;
	flex: 1 1 auto;
	min-height: 0;
	margin-top: 18px;
	gap: 18px;
}

/* 左侧目录：独立成列，不随正文滚动；条目过多时自身滚动 */
.guide-toc {
	/* 定位上下文：让目录项的 offsetTop 相对本容器计算（scrollTocIntoView 依赖） */
	position: relative;
	width: 190px;
	flex-shrink: 0;
	display: flex;
	flex-direction: column;
	overflow-y: auto;
	background: var(--app-surface);
	border: 1px solid var(--app-border);
	border-radius: 10px;
	padding: 12px 8px;
	box-sizing: border-box;
}

.guide-toc__title {
	font-size: 12px;
	color: var(--app-text-muted);
	padding: 0 10px 8px;
	border-bottom: 1px solid var(--app-border-light);
	margin-bottom: 6px;
}

.guide-toc__item {
	position: relative;
	display: flex;
	align-items: center;
	padding: 8px 10px;
	border-radius: 8px;
	font-size: 13px;
	color: var(--app-text-secondary);
	cursor: pointer;
	flex-shrink: 0;
	transition: all 0.2s ease;
}

.guide-toc__backtop {
	display: flex;
	align-items: center;
	justify-content: center;
	margin-top: auto;
	padding: 8px 10px;
	border-top: 1px solid var(--app-border-light);
	font-size: 12px;
	color: var(--app-text-secondary);
	cursor: pointer;
	flex-shrink: 0;
}

.guide-toc__backtop:hover {
	color: var(--app-primary-active);
}

.guide-toc__backtop i {
	margin-right: 5px;
	font-size: 13px;
}

.guide-toc__item:hover {
	background: var(--app-primary-soft);
	color: var(--app-primary-active);
}

/* 当前所在章节：左边加一条蓝色指示条，滚动时随内容自动切换 */
.guide-toc__item--active {
	background: var(--app-primary-soft);
	color: var(--app-primary-active);
	font-weight: 600;
}

.guide-toc__item--active::before {
	content: "";
	position: absolute;
	left: 0;
	top: 50%;
	transform: translateY(-50%);
	width: 3px;
	height: 16px;
	border-radius: 0 3px 3px 0;
	background: var(--app-primary-active);
}

.guide-toc__num {
	width: 20px;
	height: 20px;
	border-radius: 50%;
	background: var(--app-surface-sunken);
	color: var(--app-text-secondary);
	font-size: 11px;
	display: flex;
	align-items: center;
	justify-content: center;
	margin-right: 8px;
	flex-shrink: 0;
}

.guide-toc__item--active .guide-toc__num,
.guide-toc__item:hover .guide-toc__num {
	background: var(--app-primary-active);
	color: var(--app-surface);
}

/* 正文：吃掉剩余空间（不写死高度），内容超出就在这里滚动 */
.guide-content {
	flex: 1 1 auto;
	min-width: 0;
	min-height: 0;
	overflow-y: auto;
	overflow-x: hidden;
	padding-right: 6px;
	padding-bottom: 40px;
}

.guide-content::-webkit-scrollbar {
	width: 8px;
}

.guide-content::-webkit-scrollbar-thumb {
	background: var(--app-border-strong);
	border-radius: 4px;
}

.guide-content::-webkit-scrollbar-thumb:hover {
	background: var(--app-text-muted);
}

.guide-content::-webkit-scrollbar-track {
	background: transparent;
}

.guide-section {
	background: var(--app-surface);
	border: 1px solid var(--app-border);
	border-radius: 10px;
	padding: 18px 22px;
	margin-bottom: 16px;
	scroll-margin-top: 12px;
}

.guide-section__head {
	display: flex;
	align-items: center;
	padding-bottom: 12px;
	margin-bottom: 14px;
	border-bottom: 1px solid var(--app-border-light);
}

.guide-section__no {
	font-size: 12px;
	font-weight: 700;
	color: var(--app-primary-active);
	background: var(--app-primary-soft);
	border-radius: 6px;
	padding: 3px 8px;
	margin-right: 10px;
}

.guide-section__title {
	font-size: 16px;
	font-weight: 600;
	color: var(--app-text);
}

/* 条款 */
.guide-clause {
	padding: 10px 0;
}

.guide-clause + .guide-clause {
	border-top: 1px dashed var(--app-surface-sunken);
}

.guide-clause__rule {
	display: flex;
	align-items: flex-start;
	font-size: 14px;
	color: var(--app-text);
	line-height: 1.8;
}

.guide-clause__icon {
	color: var(--app-success);
	font-size: 15px;
	margin: 4px 8px 0 0;
	flex-shrink: 0;
}

.guide-clause__example {
	display: flex;
	align-items: flex-start;
	margin: 8px 0 0 23px;
	padding: 9px 12px;
	background: var(--app-warning-soft);
	border: 1px solid var(--app-warning-border);
	border-left: 3px solid var(--app-warning);
	border-radius: 6px;
	font-size: 13px;
	color: var(--app-warning-text);
	line-height: 1.7;
}

.guide-clause__example-tag {
	flex-shrink: 0;
	font-size: 12px;
	color: var(--app-warning-text);
	background: var(--app-warning-soft);
	border-radius: 4px;
	padding: 1px 7px;
	margin-right: 9px;
	margin-top: 1px;
	font-weight: 600;
}

/* 常见提示速查表 */
.guide-table {
	margin-top: 10px;
	border: 1px solid var(--app-border);
	border-radius: 8px;
	overflow: hidden;
}

.guide-table__row {
	display: flex;
	border-bottom: 1px solid var(--app-border-light);
}

.guide-table__row:last-child {
	border-bottom: none;
}

.guide-table__row--head {
	background: var(--app-surface-muted);
}

.guide-table__row--head .guide-table__cell {
	font-weight: 600;
	color: var(--app-text);
}

.guide-table__cell {
	box-sizing: border-box;
	padding: 10px 14px;
	font-size: 13px;
	line-height: 1.7;
	color: var(--app-text-secondary);
}

.guide-table__cell--no {
	width: 58px;
	flex-shrink: 0;
	text-align: center;
	color: var(--app-text-muted);
	font-weight: 600;
	border-right: 1px solid var(--app-border-light);
}

.guide-table__cell:last-child {
	flex: 1 1 auto;
	min-width: 0;
}

.guide-table__cell--msg {
	width: 38%;
	flex-shrink: 0;
	border-right: 1px solid var(--app-border-light);
	color: var(--app-warning-text);
	font-weight: 600;
}

/* 段落提示 */
.guide-section__tip {
	display: flex;
	align-items: flex-start;
	margin-top: 12px;
	padding: 10px 14px;
	background: var(--app-primary-soft);
	border: 1px solid var(--app-primary-soft);
	border-radius: 8px;
	font-size: 13px;
	color: var(--app-primary-active);
	line-height: 1.7;
}

.guide-section__tip i {
	margin: 3px 8px 0 0;
	flex-shrink: 0;
}

.guide-footer {
	margin-top: 6px;
	text-align: center;
	font-size: 12px;
	color: var(--app-text-muted);
	line-height: 1.8;
	padding-bottom: 10px;
}

/* 窄屏隐藏目录 */
@media (max-width: 900px) {
	.guide-toc {
		display: none;
	}
}
</style>
